import { OPTION_SELECT, PageTypeEnum } from '@/constant';
import {
	IAutocompleteSuggestionItem,
	TDocumentDefineItem,
	TMappingDocumentItem,
} from '@/services';
import { CommonSelectors } from '@/stores';
import { filter, map } from '@/utils';
import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { includes } from 'ramda';

import { getRelativeOptions } from '../define-document-area.config';

type RelationDocumentProps = {
	record: TDocumentDefineItem;
	pageType: PageTypeEnum;
};

const RelationDocumentMobile = ({
	pageType,
	record,
}: RelationDocumentProps) => {
	const relatedInfoOptions = useMemo(
		() => [OPTION_SELECT, ...getRelativeOptions(pageType)],
		[pageType]
	);
	const autocompleteSuggestion = useSelector(
		CommonSelectors.selectAutocompleteSuggestion
	);

	const getAutocompleteOptions = useCallback(
		(refTableName?: PageTypeEnum) => {
			let list: IAutocompleteSuggestionItem[] = [];
			switch (refTableName) {
				case PageTypeEnum.FRANCHISE:
					list = autocompleteSuggestion.franchises || [];
					break;

				case PageTypeEnum.APPLICANT:
					list = autocompleteSuggestion.applicants || [];
					break;

				case PageTypeEnum.RECOMMENDER:
					list = autocompleteSuggestion.recommenders || [];
					break;

				case PageTypeEnum.LODGING:
					list = autocompleteSuggestion.lodgings || [];
					break;

				default:
					list = [];
			}

			list = list.filter((item) => {
				const isSameId = Number(item?.id) === Number(record?.refTableId);
				const isSameTableName = record.refTableName === refTableName;
				return !(isSameId && isSameTableName);
			});

			return map(list, (item) => ({ value: item?.id, label: item?.value }));
		},
		[autocompleteSuggestion, record]
	);

	const relationDocumentList = useMemo(
		() =>
			filter(
				record?.mappingList,
				(relationDoc) =>
					includes(
						relationDoc?.refTableName,
						relatedInfoOptions.map((item) => item.value)
					) &&
					(relationDoc?.refTableName !== record.refTableName ||
						Number(relationDoc?.refTableId) !== Number(record.refTableId))
			),
		[relatedInfoOptions, record]
	);

	const getFilteredOptions = useCallback(
		(mappingItem: TMappingDocumentItem, index: number) => {
			const autocompleteOptions = getAutocompleteOptions(
				mappingItem?.refTableName
			);

			return autocompleteOptions.filter(
				(relation) =>
					!(
						Number(relation.value) === Number(record?.refTableId) &&
						mappingItem?.refTableName === record?.refTableName
					) &&
					!relationDocumentList.some(
						(valueItem, valueIndex) =>
							valueIndex !== index &&
							Number(relation.value) === Number(valueItem.refTableId)
					)
			);
		},
		[getAutocompleteOptions, relationDocumentList, record]
	);

	const renderRelationItem = useCallback(
		(
			item: TMappingDocumentItem,
			index: number,
			list: TMappingDocumentItem[]
		) => {
			const _optionsRelation = getFilteredOptions(item, index);

			const typeLabel = relatedInfoOptions?.find?.(
				(it) => it?.value === item?.refTableName
			)?.label;
			const valueShow = _optionsRelation?.find?.(
				(it) => it?.value === Number(item?.refTableId)
			)?.label;
			return (
				<>
					<div className="editable-cell cursor-text row-between gap-x-2 inline-block">
						<div className="w-full whitespace-break-spaces">
							{typeLabel && valueShow
								? `${typeLabel}(${valueShow})${index < list.length - 1 ? `, ` : ''}`
								: '-'}
						</div>
					</div>
				</>
			);
		},
		[getFilteredOptions, relatedInfoOptions]
	);

	return (
		<div className="inline-block align-top">
			{!relationDocumentList?.length
				? '-'
				: map(relationDocumentList, renderRelationItem)}
		</div>
	);
};

export default memo(RelationDocumentMobile);
